import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Spinner } from "../components/ui/kit";
import { SloupceMesice, CaraMarze, Pruhy, Podil, BARVA } from "../components/Grafy";
import { fmtKc, fmtPct } from "../lib/format";
import api from "../lib/api";
import Segment from "../components/Segment";

function Karta({ title, popis, children, testid }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <h2 className="text-[14px] font-800 text-ink">{title}</h2>
      {popis && <p className="text-[11.5px] text-muted mt-0.5 mb-3">{popis}</p>}
      {children}
    </div>
  );
}

export default function Kpi() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [mesicu, setMesicu] = useState(12);

  useEffect(() => {
    setD(null);
    api.get("/api/ekonomika/kpi", { params: { mesicu } })
      .then(({ data }) => setD(data)).catch(() => setD(false));
  }, [mesicu]);

  if (d === null) return <div className="p-10 flex justify-center"><Spinner /></div>;
  if (d === false) return <div className="card p-8 text-center text-muted">KPI se nepodařilo načíst.</div>;

  const s = d.souhrn;
  const kose = (k) => [
    { label: "Po termínu", hodnota: k.po_terminu, barva: BARVA.po_terminu },
    { label: "Do 14 dní", hodnota: k.do_14_dni, barva: BARVA.do_14 },
    { label: "Později", hodnota: k.pozdeji, barva: BARVA.pozdeji },
  ];
  const zakazka = (z) => ({
    label: z.nazev || z.cislo || "Zakázka", popis: z.klient, hodnota: z.marze,
    id: z.id, pct: z.marze_pct,
  });

  return (
    <div data-testid="kpi-page">
      <PageHeader eyebrow="Ekonomika" title="KPI"
        subtitle="Tržby, náklady a marže v čase. Peníze bez DPH, cash flow s DPH."
        actions={
          <Segment className="filtr-pas">
            {[[6, "6 měsíců"], [12, "12 měsíců"], [24, "24 měsíců"]].map(([v, l]) => (
              <button data-on={mesicu === v} key={v} onClick={() => setMesicu(v)} data-testid={`obdobi-${v}`}
                className={`filtr-seg ${mesicu === v ? "text-white" : "text-sec"}`}>{l}</button>
            ))}
          </Segment>
        } />

      <div className="rounded-card bg-ink text-white p-5 mb-4 grid grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="kpi-souhrn">
        <div><div className="label-up text-white/50 mb-1">Tržby bez DPH</div>
          <div className="amount text-[18px] font-800">{fmtKc(s.trzby)} Kč</div></div>
        <div><div className="label-up text-white/50 mb-1">Náklady bez DPH</div>
          <div className="amount text-[18px] font-800">{fmtKc(s.naklady)} Kč</div></div>
        <div><div className="label-up text-white/50 mb-1">Hrubá marže</div>
          <div className="amount text-[18px] font-800">{fmtKc(s.marze)} Kč</div></div>
        <div><div className="label-up text-white/50 mb-1">Marže v %</div>
          <div className="amount text-[18px] font-800">{fmtPct(s.marze_pct)} %</div></div>
      </div>

      <div className="space-y-4">
        <Karta title="Tržby a náklady v čase" testid="karta-mesice"
          popis="Vydané doklady proti přijatým, obojí bez DPH — daň není ani tržba, ani náklad.">
          <SloupceMesice data={d.mesice} />
        </Karta>

        <Karta title="Marže v čase" testid="karta-marze"
          popis="Kolik z každé stovky tržeb zbyde po nákladech daného měsíce.">
          <CaraMarze data={d.mesice} />
        </Karta>

        <div className="grid gap-4 lg:grid-cols-2">
          <Karta title="Co nám dluží klienti" testid="karta-pohledavky"
            popis="Neuhrazené vydané doklady včetně DPH — tolik má přijít na účet.">
            <Pruhy polozky={kose(d.pohledavky)} prazdno="Všechno je uhrazené." />
          </Karta>
          <Karta title="Co dlužíme my" testid="karta-zavazky"
            popis="Neuhrazené přijaté doklady včetně DPH.">
            <Pruhy polozky={kose(d.zavazky)} prazdno="Nic nedlužíme." />
          </Karta>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Karta title="Nejvýdělečnější zakázky" testid="karta-top"
            popis="Vyfakturováno (jinak plán z nabídky) minus skutečné náklady.">
            <Pruhy polozky={d.top.map(zakazka)} barva={BARVA.trzby} zaporna
              onKlik={(p) => nav(`/obchod/${p.id}`)} />
          </Karta>
          <Karta title="Kde plán utekl skutečnosti" testid="karta-flop"
            popis="Zakázky s nejnižší marží — tady stojí za to hledat příčinu.">
            <Pruhy polozky={d.flop.map(zakazka)} barva={BARVA.naklady} zaporna
              onKlik={(p) => nav(`/obchod/${p.id}`)}
              prazdno="Na srovnání nejhorších zakázek je jich zatím málo." />
          </Karta>
        </div>

        {d.ztraty && (
          <Karta title="Proč ztrácíme zakázky" testid="karta-ztraty"
            popis="Ztracené technologie za sledované období podle důvodu, který obchodník zadal. Hodnota je bez DPH (u nenaceněných odhad).">
            <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 t-meta text-sec" data-testid="ztraty-souhrn">
              <span>Úspěšnost <b className="amount text-ink text-[15px]">{d.ztraty.uspesnost_pct == null ? "—" : `${d.ztraty.uspesnost_pct} %`}</b></span>
              <span>vyhráno <b className="amount text-ink">{d.ztraty.vyhrano}</b></span>
              <span>ztraceno <b className="amount text-ink">{d.ztraty.ztraceno}</b></span>
            </div>
            <Pruhy polozky={d.ztraty.duvody.map((x) => ({ label: `${x.nazev} · ${x.pocet}×`, hodnota: x.hodnota, barva: BARVA.po_terminu }))}
              prazdno="Za sledované období jsme nic neztratili." />
          </Karta>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <Karta title="Náklady podle dodavatele" testid="karta-dodavatele"
            popis="Přijaté doklady bez DPH za sledované období.">
            <Pruhy polozky={(d.dodavatele || []).map((x) => ({ label: x.nazev, hodnota: x.castka }))} />
          </Karta>
          <Karta title="Materiál na zakázkách" testid="karta-material"
            popis="Ze skladu (výdejky minus vratky) proti materiálu koupenému rovnou na zakázku.">
            <Podil casti={[
              { label: "Výdej ze skladu", hodnota: d.material.ze_skladu, barva: BARVA.naklady },
              { label: "Přímý nákup na zakázku", hodnota: d.material.primy, barva: BARVA.pozdeji },
            ]} />
          </Karta>
        </div>
      </div>
    </div>
  );
}
